import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function CapitalGainsCalculator() {
  useEffect(() => {
    document.title = "Capital Gains Tax Calculator 2025 | Investment Tax Estimator - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Calculate capital gains tax on stocks, real estate, and investments. Estimate short-term and long-term capital gains taxes for 2025. Includes Schedule D calculations and tax-saving strategies for investors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'capital gains tax, investment tax calculator, Schedule D, stock tax calculator, real estate capital gains, long-term capital gains');
    }
  }, []);

  const [purchasePrice, setPurchasePrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [holdingPeriod, setHoldingPeriod] = useState('long');
  const [income, setIncome] = useState('');
  const [filingStatus, setFilingStatus] = useState('single');
  const [result, setResult] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);

  const calculate = () => {
    const purchase = parseFloat(purchasePrice);
    const sale = parseFloat(salePrice);
    const annualIncome = parseFloat(income);

    if (isNaN(purchase) || isNaN(sale) || isNaN(annualIncome)) {
      alert('Please enter valid amounts');
      return;
    }

    const capitalGain = sale - purchase;
    let taxRate = 0;
    let taxAmount = 0;

    if (holdingPeriod === 'short') {
      // Short-term: taxed as ordinary income
      if (filingStatus === 'single') {
        if (annualIncome <= 11600) taxRate = 10;
        else if (annualIncome <= 47150) taxRate = 12;
        else if (annualIncome <= 100525) taxRate = 22;
        else if (annualIncome <= 191950) taxRate = 24;
        else if (annualIncome <= 243725) taxRate = 32;
        else if (annualIncome <= 609350) taxRate = 35;
        else taxRate = 37;
      } else if (filingStatus === 'married') {
        if (annualIncome <= 23200) taxRate = 10;
        else if (annualIncome <= 94300) taxRate = 12;
        else if (annualIncome <= 201050) taxRate = 22;
        else if (annualIncome <= 383900) taxRate = 24;
        else if (annualIncome <= 487450) taxRate = 32;
        else if (annualIncome <= 731200) taxRate = 35;
        else taxRate = 37;
      } else {
        if (annualIncome <= 16550) taxRate = 10;
        else if (annualIncome <= 63100) taxRate = 12;
        else if (annualIncome <= 100500) taxRate = 22;
        else if (annualIncome <= 191950) taxRate = 24;
        else if (annualIncome <= 243700) taxRate = 32;
        else if (annualIncome <= 609350) taxRate = 35;
        else taxRate = 37;
      }
      taxAmount = capitalGain * (taxRate / 100);
    } else {
      // Long-term capital gains rates
      if (filingStatus === 'single') {
        if (annualIncome <= 47025) taxRate = 0;
        else if (annualIncome <= 518900) taxRate = 15;
        else taxRate = 20;
      } else if (filingStatus === 'married') {
        if (annualIncome <= 94050) taxRate = 0;
        else if (annualIncome <= 583750) taxRate = 15;
        else taxRate = 20;
      } else {
        if (annualIncome <= 63000) taxRate = 0;
        else if (annualIncome <= 551350) taxRate = 15;
        else taxRate = 20;
      }
      taxAmount = capitalGain * (taxRate / 100);
    }

    const netProceeds = capitalGain - taxAmount;

    setResult({
      purchasePrice: purchase,
      salePrice: sale,
      capitalGain,
      taxRate,
      taxAmount,
      netProceeds,
      holdingPeriod
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Capital Gains Tax Calculator</h1>
            <p className="text-lg text-gray-600">Calculate taxes on investment gains for stocks, real estate, and other assets</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Investment Details</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Purchase Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={purchasePrice}
                      onChange={(e) => setPurchasePrice(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="50000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sale Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={salePrice}
                      onChange={(e) => setSalePrice(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="75000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Holding Period</label>
                  <select
                    value={holdingPeriod}
                    onChange={(e) => setHoldingPeriod(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                  >
                    <option value="long">Long-term (held &gt; 1 year)</option>
                    <option value="short">Short-term (held ≤ 1 year)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Income</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={income}
                      onChange={(e) => setIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="80000"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Your taxable income for the year</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Filing Status</label>
                  <select
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                  >
                    <option value="single">Single</option>
                    <option value="married">Married Filing Jointly</option>
                    <option value="head">Head of Household</option>
                  </select>
                </div>

                <button
                  onClick={calculate}
                  className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Calculate Tax
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg text-white">
              <h2 className="text-2xl font-bold mb-6">Tax Results</h2>
              
              {result ? (
                <div className="space-y-6">
                  <div className="bg-white/10 rounded-xl p-6">
                    <div className="text-sm text-teal-100 mb-1">
                      {result.holdingPeriod === 'long' ? 'Long-Term' : 'Short-Term'} Capital Gain
                    </div>
                    <div className="text-4xl font-bold">
                      ${result.capitalGain.toLocaleString(undefined, {maximumFractionDigits: 2})}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Purchase Price</span>
                      <span className="font-semibold">${result.purchasePrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Sale Price</span>
                      <span className="font-semibold">${result.salePrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Tax Rate</span>
                      <span className="font-semibold">{result.taxRate}%</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Tax Amount</span>
                      <span className="font-semibold">${result.taxAmount.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6 mt-6">
                    <div className="text-sm text-teal-100 mb-1">Net Proceeds After Tax</div>
                    <div className="text-3xl font-bold">${result.netProceeds.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-4 text-sm">
                    <p className="text-teal-100 leading-relaxed">
                      {result.holdingPeriod === 'long' 
                        ? 'Long-term capital gains benefit from preferential tax rates (0%, 15%, or 20%) based on your income level.'
                        : 'Short-term capital gains are taxed as ordinary income at your regular tax bracket rate.'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-teal-100">
                    <i className="ri-line-chart-line text-6xl mb-4 opacity-50"></i>
                    <p>Enter your investment details to calculate capital gains tax</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">2026 Long-Term Capital Gains Tax Rates</h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border-2 border-gray-200 rounded-xl p-6">
                <div className="text-3xl font-bold text-green-600 mb-2">0%</div>
                <h4 className="font-semibold text-gray-900 mb-3">No Tax</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div>Single: Up to $47,025</div>
                  <div>Married: Up to $94,050</div>
                  <div>Head: Up to $63,000</div>
                </div>
              </div>

              <div className="border-2 border-teal-500 rounded-xl p-6 bg-teal-50">
                <div className="text-3xl font-bold text-teal-600 mb-2">15%</div>
                <h4 className="font-semibold text-gray-900 mb-3">Middle Rate</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div>Single: $47,026 - $518,900</div>
                  <div>Married: $94,051 - $583,750</div>
                  <div>Head: $63,001 - $551,350</div>
                </div>
              </div>

              <div className="border-2 border-gray-200 rounded-xl p-6">
                <div className="text-3xl font-bold text-red-600 mb-2">20%</div>
                <h4 className="font-semibold text-gray-900 mb-3">High Earners</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div>Single: Over $518,900</div>
                  <div>Married: Over $583,750</div>
                  <div>Head: Over $551,350</div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-yellow-50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <i className="ri-information-line text-2xl text-yellow-600 flex-shrink-0"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Additional Medicare Tax</h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    High-income taxpayers may owe an additional 3.8% Net Investment Income Tax (NIIT) on capital gains 
                    if their modified adjusted gross income exceeds $200,000 (single) or $250,000 (married filing jointly).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}